// Office is the only window this module talks to. Its origin is not configured
// anywhere — it is captured from the first message that provably came from the
// window framing us.
//
// event.origin is stamped by the browser and cannot be forged by the sender.
// event.source === window.parent proves the message came from our framer rather
// than a sibling frame, a popup or an opener, any of which can also post to us.
//
// Known limit: window.parent is whoever framed us, not necessarily office. A
// hostile framer passes this check. What it would learn is route paths — ids
// and timing — and useEmitRouteChange keeps credentials out of those. The
// backend still authorises every call on its own.
//
// Kept identical across modules on purpose. See office_frontend
// docs/EMBED_ROUTE_SYNC.md for the contract.

let parentOrigin = null;
let pendingPath = null;
// Docs only: the latest held-back message of each type (see postToParent).
const pendingMessages = new Map();

export function captureParentOrigin(event) {
    if (parentOrigin) return;
    if (event.source !== window.parent) return;
    // A sandboxed or file:// framer reports "null", which is not addressable.
    if (!event.origin || event.origin === "null") return;

    parentOrigin = event.origin;

    if (pendingPath !== null) {
        const path = pendingPath;
        pendingPath = null;
        emitRouteChange(path);
    }
    for (const message of pendingMessages.values()) {
        window.parent.postMessage(message, parentOrigin);
    }
    pendingMessages.clear();
}

export function emitRouteChange(path) {
    if (window.parent === window) return; // standalone, nobody to tell

    if (!parentOrigin) {
        // The first route change fires on mount, before SET_TOKEN arrives — and
        // that first path is the one worth keeping. Hold it rather than drop it.
        pendingPath = path;
        return;
    }

    // The second argument is a precondition, not an address: the browser
    // compares it against the receiving window's real origin and silently drops
    // the message on a mismatch.
    window.parent.postMessage({ type: "ROUTE_CHANGE", path }, parentOrigin);
}

// Docs only, beyond the shared contract above: tell the framer something
// happened (the signing page's DOCS_SIGNING_CHANGED). Addressed like route
// changes; dropped while the framer's origin is still unknown — by the time a
// person has acted, SET_TOKEN has long since arrived. A state the page reports
// on its own (the editor's DOCS_EDITOR_STATE, which can come before the
// framer's first message when the token was restored from storage) passes
// `{hold: true}`: the latest one of its type waits for the origin instead.
export function postToParent(message, { hold = false } = {}) {
    if (window.parent === window) return;
    if (!parentOrigin) {
        if (hold && message?.type) {
            // re-inserted so the held messages flush in the order they happened
            pendingMessages.delete(message.type);
            pendingMessages.set(message.type, message);
        }
        return;
    }
    window.parent.postMessage(message, parentOrigin);
}
